// LoginGateProvider.jsx — owns the one sign-in surface and decides when it is up.
//
// ONE WAY IT OPENS: something asks for an account. A download, the chat, a follow-up video,
// the Workspace tab. It carries the reason as its copy and it is always dismissible.
//
// The ROUTE role is gone (2026-09-13). A signed-out visitor used to meet a full-screen gate
// laid over /explore and /search before they had seen anything, which is the same mistake the
// watch wall made and it is deleted for the same reason. Account-only destinations now ask in
// place — Navigation opens this prompt instead of navigating — and a direct URL to one bounces
// home in App.jsx rather than being covered by a modal.
//
// This provider is the ONLY thing that renders LoginGate; nothing else should mount a sign-in
// surface.
import React, {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";

const LoginGate = lazy(() => import("../pages/LoginGate"));

const LoginGateContext = createContext({
  openLoginGate: () => {},
  closeLoginGate: () => {},
  isLoginGateOpen: false,
});

export const useLoginGate = () => useContext(LoginGateContext);

/**
 * Why each caller passes its own reason: "sign in to watch" and "sign in to keep creating" are
 * different promises, and a specific prompt at the moment of intent converts where a generic
 * one nags. `title`/`subtitle` override the default copy per call.
 */
export const LoginGateProvider = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [intent, setIntent] = useState(null);

  const openLoginGate = useCallback((opts = {}) => {
    setIntent({
      title: opts.title || null,
      subtitle: opts.subtitle || null,
      onSuccess: opts.onSuccess || null,
      // Caller already chose the email path, so the gate must not offer Google again.
      emailOnly: !!opts.emailOnly,
    });
  }, []);

  const closeLoginGate = useCallback(() => setIntent(null), []);

  // A prompt is about the page you are on. Leave the page and the reason for it is gone —
  // otherwise it trails the visitor around the app.
  useEffect(() => { setIntent(null); }, [location.pathname]);

  // Signing in satisfies every reason we could have been asking for.
  //
  // This briefly carried an exception for unverified email accounts, to stop the gate closing
  // over AuthForm's "check your email" step. That step is now a full-screen wall
  // (VerifyEmailWall, rendered instead of the app in App.jsx), so the exception would only
  // stack a modal on top of it. Closing on sign-in is right again.
  useEffect(() => {
    if (user && !user.isAnonymous) setIntent(null);
  }, [user]);

  const value = useMemo(
    () => ({ openLoginGate, closeLoginGate, isLoginGateOpen: !!intent }),
    [openLoginGate, closeLoginGate, intent],
  );

  return (
    <LoginGateContext.Provider value={value}>
      {children}
      {intent && (
        <Suspense fallback={null}>
          {/* Rendered alongside the app, not in place of it: the product stays visible behind
              the scrim, which is what makes this a prompt and not a wall. */}
          <LoginGate
            title={intent.title}
            subtitle={intent.subtitle}
            emailOnly={intent.emailOnly}
            onClose={closeLoginGate}
            onSuccess={() => { if (intent.onSuccess) intent.onSuccess(); closeLoginGate(); }}
          />
        </Suspense>
      )}
    </LoginGateContext.Provider>
  );
};

export default LoginGateProvider;
